import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from '../../../../../components/Modal';
import { useAssignmentContext } from '../../../context';
import { useValidateAssignmentNameMutation } from '../../../../../features/authentication/assignApiSlice';
import CustomToast from '../../../../../components/Toast';
import ConfirmationModal from '../../../../../components/ConfirmationPopUp';
import WarningAlert from './WarningAlert';
import { useActivityContext } from '../../../../activities/context';
import ActivityTypes from '../../../../../enums/activityTypes';
import { usePublishAssignmentMutation, useReplaceAssignmentMutation } from '../../../../../features/authentication/assignmentApislice';
import { usePublishActivityMutation, useReplaceActivityMutation, useValidateFlashcardNameMutation } from '../../../../../features/authentication/activityApiSlice';
import { bytesToBase64 } from '../../../../../utils/pdfUtils';

const PublishSubmitMenuModal = ({
  title,
  isOpen,
  onClose,
  textboxLabel,
  note,
  submit,
  apiParamOptions,
  helpText,
  confirmationMsg,
  fromMoreActions,
  activityType
}) => {
  const [validateAssignmentName] = useValidateAssignmentNameMutation();
  const [validateFlashcardName] = useValidateFlashcardNameMutation();
  const [publishAssignment,{isLoading : isPublishingAssignment}] = usePublishAssignmentMutation();
  const [publishActivity, {isLoading : isPublishingActivity}] = usePublishActivityMutation();
  const [replaceAssignment] = useReplaceAssignmentMutation();
  const [replaceActivity] = useReplaceActivityMutation();
  const [confirmationAlert, setConfirmationAlert] = useState(false);
  const [warningAlert, setWarningAlert] = useState(false);
  const [validatedAssignmentName, setValidatedAssignmentName] = useState('');
  const [message, setMessage] = useState('');
  const [replace, setReplace] = useState(false);
  const [assignmentName, setAssignmentName] = useState('');
  const selectedAssignment = useAssignmentContext();
  const activityContext = useActivityContext();

  const handleAssignmentName = (event) => {
    const assignmentName = event.target.value;
    setAssignmentName(assignmentName);
  };

  const validateNameAsync = async (assignmentName) => {
    if(assignmentName.length == 0){
      CustomToast('Please type a valid name', 'warning');
      return;
    }
    try {
     
      let response = {};
      if(activityType == ActivityTypes.FlashCard){
        const options = {
          publicFlashcardName: assignmentName,
          options: apiParamOptions,
          id: activityContext.assignedData[0]?.assignmentId
        };
        response = await validateFlashcardName(options).unwrap();
      }
      else
      {
        const options = {
          publicAssignmentName: assignmentName,
          options: apiParamOptions,
          assignmentId: !fromMoreActions ? selectedAssignment.assignedData[0].assignmentId : selectedAssignment.assignmentId
        };
        response = await validateAssignmentName(options).unwrap();
      }

      onClose(false);
      if (!response.showAlert) {
        setWarningAlert(false);
        setConfirmationAlert(true);
        setValidatedAssignmentName(assignmentName);
      } else {
        response.ownedSelf ? selectedAssignment.setNeverShow(true) : selectedAssignment.setSaveAs(true);
        showWarningAlert(response.showAlert);
        setReplace(response.ownedSelf);
        setMessage(response.message);
      }
    } catch (ex) {
      selectedAssignment.setSaveAs(false);
      showWarningAlert(ex.data.showAlert);
      setReplace(ex.data.ownedSelf);
      setMessage(ex.data.message);
    }
  };

  const replaceAssignmentAsync = async () => {
    try {
      const options = {
        name: bytesToBase64(new TextEncoder().encode(assignmentName)),
        option: apiParamOptions
      };
      let result = {};
      if(activityType == ActivityTypes.FlashCard)
      {
        options.id = activityContext.assignedData[0]?.assignmentId;
        result = await replaceActivity(options);
      }
      else{
        options.id = !fromMoreActions ? selectedAssignment.assignedData[0].assignmentId : selectedAssignment.assignmentId;
        result = await replaceAssignment(options);
      }
      if (!result.error) {
        CustomToast(`The ${activityType == ActivityTypes.FlashCard ? 'flashcard' : 'assignment'} has been successfully saved as a ${submit ? 'Benchmark' : 'Public'} ${activityType == ActivityTypes.FlashCard ? 'Flashcard' : 'Assignment'} named "${assignmentName}"`,'success');
        setWarningAlert(false);
        if(activityType == ActivityTypes.FlashCard)
          activityContext.setAssignedDataStatus(new Date().getSeconds());
        else{
          selectedAssignment.setAssignedDataStatus(new Date().getSeconds());
          selectedAssignment.setAssignedData([]);
          selectedAssignment.setAssignedName([]);
          selectedAssignment.setAssignmentId('');
          selectedAssignment.setAssignedCourseName([]);
        }
      }
    } catch (ex) {
      console.log(ex);
    }
  };

  const publishAssignmentAsync = async (assignmentName) => {
    try {
      const options = {
        name: bytesToBase64(new TextEncoder().encode(assignmentName)),
        option: apiParamOptions
      };

      let result = {};
      if(activityType == ActivityTypes.FlashCard)
      {
        options.id = activityContext.assignedData[0]?.assignmentId;
        result = await publishActivity(options);
      }
      else{
        options.id = !fromMoreActions ? selectedAssignment.assignedData[0].assignmentId : selectedAssignment.assignmentId;
        result = await publishAssignment(options);
      }
      if (!result.error) {
        CustomToast(`The ${activityType == ActivityTypes.FlashCard ? 'flashcard' : 'assignment'} has been successfully saved as a ${submit ? 'Benchmark' : 'Public'} ${activityType == ActivityTypes.FlashCard ? 'Flashcard' : 'Assignment'} named "${assignmentName}"`,'success');
        setConfirmationAlert(false);
        setWarningAlert(false);
        onClose(false);
        if(activityType == ActivityTypes.FlashCard){
          activityContext.setAssignedDataStatus(new Date().getSeconds());
        }
        else{
          selectedAssignment.setNeverShow(true);
          selectedAssignment.setSaveAs(false);
          selectedAssignment.setAssignedDataStatus(new Date().getSeconds());
          selectedAssignment.setAssignedData([]);
          selectedAssignment.setAssignedName([]);
          selectedAssignment.setAssignmentId('');
          selectedAssignment.setAssignedCourseName([]);
        }
      }
    } catch (ex) {
      console.log(ex);
    }
  };

  const showConfirmation = (confirmMsg) => {
    if (confirmMsg) {
      setAssignmentName(validatedAssignmentName);
      publishAssignmentAsync(validatedAssignmentName);
    } else {
      onClose(true);
      setConfirmationAlert(false); //not publish and close cofirmation alert
      selectedAssignment.setNeverShow(true);
      selectedAssignment.setSaveAs(false);
    }
  };

  const showWarningAlert = (alert) => {
    setWarningAlert(alert);
    alert ? onClose(false) : onClose(true);
  };

  useEffect(() => {
    if(activityType == ActivityTypes.FlashCard)
      setAssignmentName(activityContext.assignedData[0]?.assignmentName);
    else
      selectedAssignment.assignedName.map((item) => setAssignmentName(item.trim()));
  }, [isOpen]);

  return (
    <>
      <Modal
        contentWidth="1000px"
        isOpen={isOpen}
        titleName={title}
        onClose={() => onClose(false)}
        titleToolTip={helpText}>
        <div className="d-flex align-items-center justify-content-between name-display-box">
          <div className="display-label">
            Your {activityType == ActivityTypes.FlashCard ? 'flashcard' : 'assignment'}:{' '}
            <span className="display-name">{activityType == ActivityTypes.FlashCard ? activityContext.assignedData[0]?.assignmentName : selectedAssignment.assignedName[0]}</span>
          </div>
          <div className="display-label">
            Course: <span className="display-name">{activityType == ActivityTypes.FlashCard ? activityContext.assignedData[0]?.courseName : selectedAssignment.assignedCourseName[0]}</span>
          </div>
        </div>
        <div className="my-4">
          <label className="form-label">{textboxLabel}</label>
          <input
            type="text"
            className="form-control"
            value={assignmentName}
            onChange={(e) => handleAssignmentName(e)}
          />
        </div>
        {submit && <p>{note}</p>}
        <div className="col-12 d-flex justify-content-end mt-5">
          <button className="btn btn-castleGray me-2" onClick={() => onClose(false)}>
            Cancel
          </button>
          <button
            className="btn btn-castleRed"
            onClick={() =>  validateNameAsync(assignmentName)}
            disabled={isPublishingAssignment || isPublishingActivity}>
            Publish
          </button>
        </div>
      </Modal>
      <ConfirmationModal
        isOpen={confirmationAlert}
        onClose={() => showConfirmation(false)}
        onSave={() => showConfirmation(true)}
        isLoading={isPublishingAssignment || isPublishingActivity}
        contentWidth={'35%'}
        buttonPosition={'center'}
        content={confirmationMsg}
        cancelButton={'No'}
        confirmButton={'Yes'}
        titleName={'Confirm'}
      />
      <WarningAlert
        isOpen={warningAlert}
        onClose={() => showWarningAlert(false)}
        isLoading={isPublishingAssignment || isPublishingActivity}
        replaceName={replace}
        contentWidth={'40%'}
        content={message}
        setContent={setMessage}
        saveButton={'Save'}
        cancelButton={'Cancel'}
        replaceData={replaceAssignmentAsync}
        validateData={validateNameAsync}
        assignmentName={assignmentName}
        activityType={activityType}
      />
    </>
  );
};

PublishSubmitMenuModal.propTypes = {
  title: PropTypes.string.isRequired,
  textboxLabel: PropTypes.string.isRequired,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  note: PropTypes.string,
  submit: PropTypes.bool,
  apiParamOptions: PropTypes.number,
  helpText: PropTypes.string,
  confirmationMsg: PropTypes.string,
  fromMoreActions: PropTypes.bool,
  activityType: PropTypes.number
};

export default PublishSubmitMenuModal;
